import { create } from 'zustand';
import type { MemberDto, TeamState } from '@timato/shared';
import { useAuth } from '@/auth/store';
import { teamApi, type Membership } from './api';
import { clearTeamMirror, mirrorTeamState } from './cache';
import { toast } from 'sonner';

type TeamStore = {
  /** undefined = not loaded yet; null = no team. */
  membership: Membership | null | undefined;
  state: TeamState | null;
  etag: string | null;
  error: string | null;
  load: () => Promise<void>;
  refresh: () => Promise<void>;
  setMembership: (m: Membership | null) => void;
  reset: () => Promise<void>;
};

/** Team-plane state for the signed-in user; polled with ETag while the tab is visible (architecture §9). */
const NO_MEMBERS: MemberDto[] = [];
/** Stable empty array so selectors keep a steady reference. */
export const selectMembers = (s: TeamStore) => s.state?.members ?? NO_MEMBERS;

export const useTeam = create<TeamStore>((set, get) => ({
  membership: undefined, state: null, etag: null, error: null,

  async load() {
    if (useAuth.getState().status !== 'signedIn') { set({ membership: null, state: null }); return; }
    try {
      const m = await teamApi.mine();
      set({ membership: m });
      if (m) await get().refresh();
    } catch (e) { set({ error: (e as Error).message }); }
  },

  async refresh() {
    const m = get().membership;
    if (!m) return;
    try {
      const r = await teamApi.state(m.team.id, get().etag);
      if (!r) return; // 304
      notifyForcedReleases(get().state, r.state);
      set({ state: r.state, etag: r.etag, membership: { team: r.state.team, role: r.state.myRole }, error: null });
      await mirrorTeamState(r.state);
    } catch (e) { set({ error: (e as Error).message }); }
  },

  setMembership(m) { set({ membership: m, state: m ? get().state : null, etag: null }); },

  async reset() {
    const id = get().membership?.team.id ?? get().state?.team.id;
    if (id) await clearTeamMirror(id);
    set({ membership: null, state: null, etag: null, error: null });
  },
}));

/** A task I held that is now free was force-released by an admin; say so rather than letting it vanish silently. */
function notifyForcedReleases(before: TeamState | null, after: TeamState) {
  const me = useAuth.getState().user?.id;
  if (!before || !me) return;
  for (const was of before.tasks) {
    if (was.claimedBy !== me) continue;
    const now = after.tasks.find((t) => t.id === was.id);
    if (now && !now.claimedBy && !now.finishedAt) {
      toast(`“${was.name}” was released`, { description: 'An admin freed it up so someone else can pick it up. Your logged time stays with the task.' });
    }
  }
}
